import { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import Header from "@/components/marqolix/Header";
import KPIGrid from "@/components/marqolix/KPIGrid";
import ExchangesGrid from "@/components/marqolix/ExchangesGrid";
import PairSelector from "@/components/marqolix/PairSelector";
import CompliancePanel from "@/components/marqolix/CompliancePanel";
import OrderBook from "@/components/marqolix/OrderBook";
import ControlPanel from "@/components/marqolix/ControlPanel";
import AlertLog from "@/components/marqolix/AlertLog";
import AuditLog from "@/components/marqolix/AuditLog";
import CapitalAllocator from "@/components/marqolix/CapitalAllocator";
import FillsTable from "@/components/marqolix/FillsTable";
import ApiAuditLog from "@/components/marqolix/ApiAuditLog";
import DiagnosticsPanel from "@/components/marqolix/DiagnosticsPanel";
import HealthMonitor from "@/components/marqolix/HealthMonitor";
import UserManagement from "@/components/marqolix/UserManagement";
import Wallets from "@/components/marqolix/Wallets";
import ConfigEditor from "@/components/marqolix/ConfigEditor";
import Charts from "@/components/marqolix/Charts";
import OnChainPanel from "@/components/marqolix/OnChainPanel";
import WorkerStatusPanel from "@/components/marqolix/WorkerStatusPanel";
import AdaptiveSizingPanel from "@/components/marqolix/AdaptiveSizingPanel";
import PipelineTracePanel from "@/components/marqolix/PipelineTracePanel";

export default function Dashboard() {
  const [state, setState] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const wsRef = useRef(null);

  const loadOnce = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/bot/status"),
        api.get("/config"),
      ]);
      setState({ ...s.data, cfg_buy_pct_of_ask: c.data.buy_pct_of_ask });
    } catch (err) {
      // 401 → AuthContext will redirect; anything else is a transient network
      // blip that the next poll will recover from. Surface for debugging.
      if (err?.response?.status !== 401) {
        console.warn("Dashboard loadOnce failed:", err?.message || err);
      }
    }
  };

  const refreshAll = async () => {
    // Called after any mutation that may change state.symbol / state.mode /
    // thresholds. WS may be down through the ingress; an explicit re-fetch
    // guarantees the UI reflects the change immediately.
    await loadOnce();
  };

  useEffect(() => {
    loadOnce();
    // open WS — cookie is sent automatically (iter14 — no token in URL).
    // We keep the query-param empty so the backend takes the cookie path.
    const wsUrl = BACKEND_URL.replace(/^http/, "ws") + "/api/ws";
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    let pollId = null;

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        setState((prev) => ({ ...msg, cfg_buy_pct_of_ask: prev?.cfg_buy_pct_of_ask }));
      } catch (err) {
        console.warn("WS message parse failed:", err?.message || err);
      }
    };
    ws.onclose = () => {
      // Fallback to polling if WS drops
      pollId = setInterval(loadOnce, 2500);
    };

    return () => {
      try { ws.close(); } catch (err) {
        console.warn("WS close failed:", err?.message || err);
      }
      if (pollId) clearInterval(pollId);
    };
  }, []);

  const onCfgUpdate = async () => {
    await refreshAll();
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--mq-bg)" }}>
      <Header state={state} />

      <div className="px-4 sm:px-6 py-4 space-y-4">
        <PairSelector state={state} onChange={onCfgUpdate} />
        <KPIGrid state={state} />
        <ExchangesGrid state={state} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-4">
            <OrderBook state={state} />
          </div>
          <div className="lg:col-span-4 space-y-4">
            <CompliancePanel state={state} />
            <OnChainPanel state={state} />
            <CapitalAllocator />
            <ControlPanel state={state} onConfigUpdate={onCfgUpdate} />
            <Wallets state={state} />
          </div>
          <div className="lg:col-span-4">
            <AlertLog />
          </div>
        </div>

        <Charts />

        <PipelineTracePanel state={state} />

        <AdaptiveSizingPanel />

        <FillsTable state={state} />

        <HealthMonitor />

        <WorkerStatusPanel />

        <DiagnosticsPanel />

        <ApiAuditLog />

        <UserManagement />

        <AuditLog />

        <div className="flex justify-end">
          <button
            data-testid="toggle-config-button"
            className="mq-btn"
            onClick={() => setShowConfig((v) => !v)}
          >
            {showConfig ? "▲ Hide config" : "▼ Show config editor"}
          </button>
        </div>

        {showConfig && <ConfigEditor onConfigUpdate={onCfgUpdate} />}

        <div className="text-center text-[10px] text-[var(--mq-text-3)] tracking-widest uppercase py-4">
          {(() => {
            const active = state?.active_exchanges || [];
            if (state?.no_active_exchange || active.length === 0) {
              return `Marqolix NG · v1.1.0 · NO ACTIVE EXCHANGE`;
            }
            const label = active.map((x) => x.name).join(" · ").toUpperCase();
            const anyLive = active.some((x) => x.mode === "LIVE");
            return `Marqolix NG · v1.1.0 · ${label} · ${anyLive ? "LIVE" : (state?.testnet ? "TESTNET" : "DRY-RUN")}`;
          })()}
        </div>
      </div>
    </div>
  );
}
